/**
 * Admin discount oversight — closes gap #2 from the audit.
 * Platform-wide view of every merchant's active + past discount
 * campaigns, with redemption analytics per code + per merchant.
 *
 * Route: /discounts  (top-level, admin-side)
 * Perm gate: discounts.read (adminRbacPrisma).
 * CSS: scoped with `do-*` per CLAUDE.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './DiscountOversight.css';

const api = (path, opts = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL || '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
};

const STATUS_META = {
  active:    { color: '#059669', label: 'Active' },
  scheduled: { color: '#0891b2', label: 'Scheduled' },
  expired:   { color: '#6b7280', label: 'Expired' },
  inactive:  { color: '#dc2626', label: 'Inactive' },
};

function fmtNaira(v) {
  const n = Number(v) || 0;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}
function fmtDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.inactive;
  return (
    <span className="do-pill" style={{ background: `${meta.color}18`, color: meta.color }}>
      <span className="do-pill__dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Main page                                                              */
/* ══════════════════════════════════════════════════════════════════════ */

export default function DiscountOversight() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const merchantIdFilter = searchParams.get('merchantId') || '';

  const [rows, setRows] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({
    status: 'active', campaignType: '', autoApply: '', search: '',
  });

  const loadList = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page',  String(opts.page  || pagination.page));
      params.set('limit', String(opts.limit || pagination.limit));
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (merchantIdFilter) params.set('merchantId', merchantIdFilter);

      const res  = await api(`/admin/discounts?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setRows(json.data || []);
      setPagination((p) => ({ ...p, ...json.pagination }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, merchantIdFilter]);

  const loadAnalytics = useCallback(async () => {
    try {
      const res  = await api('/admin/discounts/analytics');
      const json = await res.json();
      if (res.ok && json?.success) setAnalytics(json);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadList({ page: 1 }); }, [filters, merchantIdFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const setFilter = (key, value) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((cur) => ({ ...cur, [key]: value }));
  };
  const clearFilters = () => {
    setFilters({ status: '', campaignType: '', autoApply: '', search: '' });
  };
  const hasFilters = useMemo(
    () => Object.values(filters).some((v) => v && String(v).length > 0),
    [filters]
  );

  const clearMerchantFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('merchantId');
    setSearchParams(next);
  };

  return (
    <div className="do-page">
      <header className="do-page__head">
        <div>
          <h1 className="do-page__title">Discount oversight</h1>
          <p className="do-page__subtitle">
            Every merchant's discount campaigns + redemption analytics.
            {merchantIdFilter && (
              <>
                {' '}
                Filtered to merchant <code className="do-mono">{merchantIdFilter.slice(0, 8)}…</code>
                <button type="button" className="do-btn do-btn--ghost do-btn--sm" onClick={clearMerchantFilter}>
                  clear
                </button>
              </>
            )}
          </p>
        </div>
      </header>

      {/* ── Analytics summary ──────────────────────────── */}
      {analytics && (
        <div className="do-analytics">
          <SummaryCard label="Active campaigns" value={analytics.totals.activeCampaigns.toLocaleString('en-NG')} />
          <SummaryCard label={`Redemptions (${fmtDate(analytics.window.from)}→now)`} value={analytics.totals.redemptions.toLocaleString('en-NG')} />
          <SummaryCard label="Total discounted" value={fmtNaira(analytics.totals.totalDiscounted)} tone="warn" />
          <SummaryCard label="Order value covered" value={fmtNaira(analytics.totals.totalOrderValue)} />
        </div>
      )}

      {analytics && (analytics.topMerchants.length > 0 || analytics.topCodes.length > 0) && (
        <div className="do-lists">
          <div className="do-list-card">
            <h3>Top merchants by discount spend</h3>
            {analytics.topMerchants.length === 0 && <p className="do-list-empty">No redemptions yet.</p>}
            {analytics.topMerchants.map((m) => (
              <div key={m.merchantId} className="do-list-row">
                <div className="do-list-row__label">
                  <strong>{m.merchant?.storeName || m.merchant?.email || m.merchantId.slice(0, 8)}</strong>
                  <span className="do-list-row__sub">{m.redemptions.toLocaleString('en-NG')} redemptions</span>
                </div>
                <span className="do-list-row__value">{fmtNaira(m.totalDiscounted)}</span>
              </div>
            ))}
          </div>
          <div className="do-list-card">
            <h3>Top codes by redemption count</h3>
            {analytics.topCodes.length === 0 && <p className="do-list-empty">No redemptions yet.</p>}
            {analytics.topCodes.map((c) => (
              <div key={c.code} className="do-list-row">
                <div className="do-list-row__label">
                  <strong className="do-mono">{c.code}</strong>
                  <span className="do-list-row__sub">{c.redemptions.toLocaleString('en-NG')} redemptions</span>
                </div>
                <span className="do-list-row__value">{fmtNaira(c.totalDiscounted)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filters ───────────────────────────────────── */}
      <div className="do-filters">
        <input
          type="text"
          className="do-input"
          placeholder="Search by code or name…"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <select className="do-select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="scheduled">Scheduled</option>
          <option value="expired">Expired</option>
          <option value="inactive">Inactive</option>
        </select>
        <select className="do-select" value={filters.campaignType} onChange={(e) => setFilter('campaignType', e.target.value)}>
          <option value="">All types</option>
          <option value="COUPON">Coupon</option>
          <option value="SALE">Sale</option>
        </select>
        <select className="do-select" value={filters.autoApply} onChange={(e) => setFilter('autoApply', e.target.value)}>
          <option value="">Auto-apply: any</option>
          <option value="true">Auto-apply only</option>
          <option value="false">Coupon only</option>
        </select>
        {hasFilters && (
          <button type="button" className="do-btn do-btn--ghost" onClick={clearFilters}>Clear</button>
        )}
        <button type="button" className="do-btn" onClick={() => loadList()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="do-error">{error}</div>}

      {/* ── Table ─────────────────────────────────────── */}
      <div className="do-table-wrap">
        <table className="do-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Merchant</th>
              <th>Type</th>
              <th className="do-num">Value</th>
              <th>Status</th>
              <th>Auto</th>
              <th className="do-num">Used</th>
              <th>Starts</th>
              <th>Ends</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={10} className="do-empty">No campaigns match these filters.</td></tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} className="do-row" onClick={() => setDetailId(d.id)}>
                <td>
                  <div>
                    <strong className="do-mono">{d.code}</strong>
                    {d.name && <div className="do-cell-meta">{d.name}</div>}
                  </div>
                </td>
                <td>
                  <div>{d.merchant?.storeName || d.merchant?.email || '—'}</div>
                  {d.merchant?.storeSlug && <div className="do-cell-meta">/{d.merchant.storeSlug}</div>}
                </td>
                <td><span className="do-chan">{d.campaignType}</span></td>
                <td className="do-num">
                  {d.type === 'PERCENTAGE' ? `${d.value}%` : fmtNaira(d.value)}
                </td>
                <td><StatusPill status={d.derivedStatus} /></td>
                <td>{d.autoApply ? '✓' : '—'}</td>
                <td className="do-num">
                  {d.currentRedemptions}{d.maxRedemptions > 0 ? ` / ${d.maxRedemptions}` : ''}
                </td>
                <td>{fmtDate(d.startsAt)}</td>
                <td>{d.endsAt ? fmtDate(d.endsAt) : <span className="do-muted">—</span>}</td>
                <td><button type="button" className="do-view">View →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ────────────────────────────────── */}
      {pagination.total > 0 && (
        <div className="do-pagination">
          <span>
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString('en-NG')}
          </span>
          <button
            type="button"
            className="do-btn"
            disabled={pagination.page <= 1 || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page - 1 })); loadList({ page: pagination.page - 1 }); }}
          >Prev</button>
          <button
            type="button"
            className="do-btn"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page + 1 })); loadList({ page: pagination.page + 1 }); }}
          >Next</button>
        </div>
      )}

      {/* ── Detail drawer ─────────────────────────────── */}
      {detailId && <DiscountDetailDrawer discountId={detailId} onClose={() => setDetailId(null)} navigate={navigate} />}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  return (
    <div className={`do-summary ${tone ? `do-summary--${tone}` : ''}`}>
      <div className="do-summary__label">{label}</div>
      <div className="do-summary__value">{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Detail drawer                                                          */
/* ══════════════════════════════════════════════════════════════════════ */

function DiscountDetailDrawer({ discountId, onClose, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoad(true);
      setError('');
      try {
        const res  = await api(`/admin/discounts/${discountId}`);
        const json = await res.json();
        if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
        if (!cancelled) setData(json.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discountId]);

  const d = data?.discount;
  const m = data?.merchant;
  const t = data?.totals;

  return (
    <div className="do-drawer-scrim" onClick={onClose}>
      <aside className="do-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="do-drawer__head">
          <div>
            <h3>Discount detail</h3>
            {d && <p className="do-drawer__sub"><span className="do-mono">{d.code}</span> · {d.name || 'Untitled'}</p>}
          </div>
          <button type="button" className="do-btn do-btn--ghost" onClick={onClose}>Close</button>
        </header>

        <div className="do-drawer__body">
          {loading && <div className="do-loading">Loading…</div>}
          {error && <div className="do-error">{error}</div>}
          {d && (
            <>
              {/* Rollup */}
              <div className="do-detail-block">
                <div className="do-detail-kv"><span>Status</span><strong><StatusPill status={d.derivedStatus} /></strong></div>
                <div className="do-detail-kv"><span>Type / value</span><strong>{d.type} · {d.type === 'PERCENTAGE' ? `${d.value}%` : fmtNaira(d.value)}</strong></div>
                <div className="do-detail-kv"><span>Campaign type</span><strong>{d.campaignType}</strong></div>
                <div className="do-detail-kv"><span>Auto-apply</span><strong>{d.autoApply ? 'Yes' : 'No'}</strong></div>
                <div className="do-detail-kv"><span>Public</span><strong>{d.isPublic ? 'Yes' : 'No'}</strong></div>
                <div className="do-detail-kv"><span>Priority</span><strong>{d.priority}</strong></div>
              </div>

              {/* Limits */}
              <div className="do-detail-block">
                <h4>Limits</h4>
                <div className="do-detail-kv"><span>Min order</span><strong>{d.minOrderAmount ? fmtNaira(d.minOrderAmount) : '—'}</strong></div>
                <div className="do-detail-kv"><span>Max discount</span><strong>{d.maxDiscountAmount ? fmtNaira(d.maxDiscountAmount) : '—'}</strong></div>
                <div className="do-detail-kv"><span>Max redemptions</span><strong>{d.maxRedemptions > 0 ? d.maxRedemptions.toLocaleString('en-NG') : 'Unlimited'}</strong></div>
                <div className="do-detail-kv"><span>Per customer</span><strong>{d.usagePerCustomer}</strong></div>
              </div>

              {/* Window */}
              <div className="do-detail-block">
                <h4>Window</h4>
                <div className="do-detail-kv"><span>Starts</span><strong>{fmtDateTime(d.startsAt)}</strong></div>
                <div className="do-detail-kv"><span>Ends</span><strong>{d.endsAt ? fmtDateTime(d.endsAt) : 'No end date'}</strong></div>
              </div>

              {/* Redemption totals */}
              <div className="do-detail-block">
                <h4>Redemptions</h4>
                <div className="do-detail-kv"><span>Count</span><strong>{(t?.redemptions ?? 0).toLocaleString('en-NG')}</strong></div>
                <div className="do-detail-kv"><span>Total discounted</span><strong>{fmtNaira(t?.totalDiscounted)}</strong></div>
                <div className="do-detail-kv"><span>Order value covered</span><strong>{fmtNaira(t?.totalOrderValue)}</strong></div>
              </div>

              {/* Scope */}
              {(d.applicableProducts?.length > 0 || d.applicableCategories?.length > 0 || d.excludedProducts?.length > 0 || d.excludedCategories?.length > 0) && (
                <div className="do-detail-block">
                  <h4>Scope</h4>
                  {d.applicableProducts?.length > 0 && (
                    <div className="do-detail-kv"><span>Applicable products</span><strong>{d.applicableProducts.length}</strong></div>
                  )}
                  {d.applicableCategories?.length > 0 && (
                    <div className="do-detail-kv"><span>Applicable categories</span><strong>{d.applicableCategories.join(', ')}</strong></div>
                  )}
                  {d.excludedProducts?.length > 0 && (
                    <div className="do-detail-kv"><span>Excluded products</span><strong>{d.excludedProducts.length}</strong></div>
                  )}
                  {d.excludedCategories?.length > 0 && (
                    <div className="do-detail-kv"><span>Excluded categories</span><strong>{d.excludedCategories.join(', ')}</strong></div>
                  )}
                </div>
              )}

              {/* Merchant */}
              {m && (
                <div className="do-detail-block">
                  <h4>Merchant</h4>
                  <div className="do-detail-kv"><span>Store</span><strong>{m.storeName || '—'}{m.storeSlug ? ` (/${m.storeSlug})` : ''}</strong></div>
                  <div className="do-detail-kv"><span>Email</span><strong>{m.email}</strong></div>
                  <div className="do-detail-kv"><span>Tier</span><strong>{m.tier}</strong></div>
                  <div className="do-actions">
                    <button
                      type="button"
                      className="do-btn"
                      onClick={() => navigate(`/merchants/${m.id}/orders`)}
                    >
                      View merchant orders
                    </button>
                  </div>
                </div>
              )}

              {/* Recent redemptions */}
              {data?.redemptions?.length > 0 && (
                <div className="do-detail-block">
                  <h4>Recent redemptions ({data.redemptions.length})</h4>
                  <div className="do-redemptions">
                    {data.redemptions.slice(0, 20).map((r) => (
                      <div key={r.id} className="do-redemption">
                        <div>
                          <span className="do-mono">order {r.orderId.slice(0, 8)}</span>
                          <div className="do-cell-meta">{fmtDateTime(r.createdAt)}</div>
                        </div>
                        <div className="do-redemption__amounts">
                          <strong>-{fmtNaira(r.amountDiscounted)}</strong>
                          <span className="do-cell-meta">on {fmtNaira(r.orderSubtotal)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {data.redemptions.length === 20 && (
                    <p className="do-cell-meta">Showing 20 most recent — DiscountRedemption table has more.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
